import { Injectable } from '@angular/core'
import { Select, State, Action, StateContext, Selector, NgxsOnInit, Store, NgxsSimpleChange } from '@ngxs/store'
import { Observable } from 'rxjs';
import { UserState } from 'src/app/state-man/state/user.state';
import { User } from 'src/app/state-man/models/user.model';
import { SubCategoryActions } from '../actions/sub-category.actions';
import { SubCategory } from '../models/sub-category.model';
import { SubCategoryService } from 'src/app/services/sub-category.service';
import { StorageService } from 'src/app/services/storage.service';
import { stateContentChanged } from './state-content-changed'

export class SubCategoryStateModel {
    is_refreshing: boolean
    sub_category: SubCategory[]
    version?: string
}

@State<SubCategoryStateModel>({
    name: 'sub_category',
    defaults: {
        is_refreshing: true,
        sub_category: []
    }
})
@Injectable()
export class SubCategoryState implements NgxsOnInit {
    @Select(UserState.getUser) user$: Observable<User>

    constructor(        
        private subCategoryService: SubCategoryService,
        private store: Store,
        private storageService: StorageService
    ) {}

    async ngxsOnInit(ctx: StateContext<SubCategoryStateModel>) {
        this.user$.subscribe(async (user: User) => {            
            if (user.is_logged_in) {     
                this.storageService.events.subscribe(async (status: any) => {
                    if (this.storageService.is_initialized) {                        
                        await this.reloadStateFromStorage()                        
                    }
                })                           
            }
        }) 
    }

    async ngxsOnChanges(change: NgxsSimpleChange) {           
        if (this.storageService.is_initialized && stateContentChanged(change)) {   
            const state_model: SubCategoryStateModel = change.currentValue
            this.storageService.set('sub_categories', JSON.stringify(state_model))
        }    
    }

    async reloadStateFromStorage() {        
        const json: string = await this.storageService.get('categories');
        if (json) {
            const state_model: SubCategoryStateModel = JSON.parse(json)
            if (state_model) {                
                this.store.dispatch(new SubCategoryActions.Init(state_model));
                return
            } 
        }
        this.store.dispatch(new SubCategoryActions.Refresh())
    }

    async reloadStateFromApi() : Promise<boolean> {    
        // Send the version only with rows to go with it.
        const held = this.store.selectSnapshot(SubCategoryState.getState)
        const ret = await this.subCategoryService.getAll(held.sub_category?.length ? held.version ?? '' : '')
        if (ret?.rows) {
          // The service has already run buildMany over the response, so the
          // rows arriving here are built models. Building them a second time
          // allocated a duplicate object for every row and, where buildOne
          // rebuilds children, re-walked those too.
          this.store.dispatch(new SubCategoryActions.Set(ret.rows, ret.version))
          return true
        }
        // Failed, or the list held is still current.
        return false
    }
  
    @Selector()
    static getState(state: SubCategoryStateModel) {
        return state
    }

    @Selector()
    static getIsRefreshing(state: SubCategoryStateModel) {
        return state.is_refreshing
    }

    @Selector()
    static getAll(state: SubCategoryStateModel) {
        return state.sub_category
    }

    @Action(SubCategoryActions.Refresh)
    async refresh({patchState}: StateContext<SubCategoryStateModel>, {  }:SubCategoryActions.Refresh) {
        patchState({is_refreshing: true})
        if (!await this.reloadStateFromApi()) {            
            patchState({is_refreshing: false})
        }
    }
    
    @Action(SubCategoryActions.Init)
    init({setState, getState}: StateContext<SubCategoryStateModel>, { payload }:SubCategoryActions.Init) {
        const state = getState()
        payload.is_refreshing = state.is_refreshing
        setState(payload)
        this.store.dispatch(new SubCategoryActions.Refresh())
    }

    @Action(SubCategoryActions.Set)
    set({setState}: StateContext<SubCategoryStateModel>, { payload, version }:SubCategoryActions.Set) {
        setState({    
            is_refreshing: false,        
            sub_category: payload,
            version
        })
    }
}
